import { defineStore } from 'pinia'
import { ref } from 'vue'

export type ToastType = 'success' | 'error' | 'info' | 'warning'

export interface ToastMessage {
  id: string
  type: ToastType
  text: string
  duration?: number
}

const MAX_TOASTS = 4

export const useToastStore = defineStore('toast', () => {
  const toasts = ref<ToastMessage[]>([])

  function addToast(message: Omit<ToastMessage, 'id'>) {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`

    // Add the new toast to the array
    toasts.value.push({
      id,
      ...message,
    })
    if (toasts.value.length > MAX_TOASTS) {
      toasts.value.splice(0, toasts.value.length - MAX_TOASTS)
    }

    // For success, warning, info, and error messages, automatically remove after duration
    if (
      (message.type === 'success' ||
        message.type === 'warning' ||
        message.type === 'info' ||
        message.type === 'error') &&
      message.duration !== 0
    ) {
      let duration = message.duration

      // Set default durations for different toast types
      if (!duration) {
        switch (message.type) {
          case 'success':
            duration = 5000
            break
          case 'info':
            duration = 6000
            break
          case 'warning':
            duration = 8000
            break
          case 'error':
            duration = 15000 // 15 seconds for error toasts
            break
          default:
            duration = 5000
        }
      }

      setTimeout(() => {
        removeToast(id)
      }, duration)
    }

    return id
  }

  function removeToast(id: string) {
    const index = toasts.value.findIndex((toast) => toast.id === id)
    if (index !== -1) {
      toasts.value.splice(index, 1)
    }
  }

  function clearToasts() {
    toasts.value = []
  }

  return {
    toasts,
    addToast,
    removeToast,
    clearToasts,
  }
})
